import { useCallback, useRef, useState } from 'react';
import { Conversation } from '@elevenlabs/client';

export type ElevenLabsStatus = 'idle' | 'connecting' | 'connected' | 'disconnected';
export type ElevenLabsMode = 'listening' | 'speaking';

export interface ElevenLabsMessage {
  source: 'user' | 'agent';
  message: string;
}

export interface UseElevenLabsConversationOptions {
  /** Signed URL from the backend */
  signedUrl: string;
  /** Dynamic context to send on connect */
  context?: string;
  /** Called when connection status changes */
  onStatusChange?: (status: ElevenLabsStatus) => void;
  /** Called when mode changes (listening/speaking) */
  onModeChange?: (mode: ElevenLabsMode) => void;
  /** Called when a new message (transcript) is received */
  onMessage?: (message: ElevenLabsMessage) => void;
  /** Called when an error occurs */
  onError?: (error: Error) => void;
  /** Called when the conversation ends */
  onDisconnect?: () => void;
}

export interface UseElevenLabsConversationReturn {
  /** Current connection status */
  status: ElevenLabsStatus;
  /** Current mode (listening or speaking) */
  mode: ElevenLabsMode;
  /** Whether the microphone is muted */
  isMuted: boolean;
  /** Connect to the ElevenLabs agent */
  connect: () => Promise<void>;
  /** Disconnect from the conversation */
  disconnect: () => Promise<void>;
  /** Toggle microphone mute */
  toggleMute: () => void;
  /** Set microphone mute state */
  setMuted: (muted: boolean) => void;
  /** Get input volume level (0-1) for visualization */
  getInputVolume: () => number;
  /** Get output volume level (0-1) for visualization */
  getOutputVolume: () => number;
}

/**
 * Hook for managing an ElevenLabs Conversational AI session.
 * Uses the ElevenLabs SDK with WebSocket connection and VAD (Voice Activity Detection).
 */
export function useElevenLabsConversation(
  options: UseElevenLabsConversationOptions
): UseElevenLabsConversationReturn {
  const [status, setStatus] = useState<ElevenLabsStatus>('idle');
  const [mode, setMode] = useState<ElevenLabsMode>('listening');
  const [isMuted, setIsMuted] = useState(false);

  const conversationRef = useRef<Conversation | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const connect = useCallback(async () => {
    if (conversationRef.current) {
      console.warn('Already connected to ElevenLabs');
      return;
    }

    try {
      setStatus('connecting');

      // Request microphone permission first
      await navigator.mediaDevices.getUserMedia({ audio: true });

      const conversation = await Conversation.startSession({
        signedUrl: optionsRef.current.signedUrl,
        onConnect: () => {
          setStatus('connected');
          optionsRef.current.onStatusChange?.('connected');

          // Send dynamic context after connection
          if (optionsRef.current.context) {
            conversation.sendContextualUpdate(optionsRef.current.context);
          }
        },
        onDisconnect: () => {
          setStatus('disconnected');
          optionsRef.current.onStatusChange?.('disconnected');
          optionsRef.current.onDisconnect?.();
          conversationRef.current = null;
        },
        onError: (message: string) => {
          console.error('ElevenLabs conversation error:', message);
          optionsRef.current.onError?.(new Error(message));
        },
        onModeChange: (newMode: { mode: 'listening' | 'speaking' }) => {
          const modeValue = newMode.mode;
          setMode(modeValue);
          optionsRef.current.onModeChange?.(modeValue);
        },
        onMessage: (message: { source: 'user' | 'ai'; message: string }) => {
          optionsRef.current.onMessage?.({
            source: message.source === 'ai' ? 'agent' : 'user',
            message: message.message,
          });
        },
      });

      conversationRef.current = conversation;
    } catch (error) {
      console.error('Failed to connect to ElevenLabs:', error);
      setStatus('idle');
      optionsRef.current.onError?.(
        error instanceof Error ? error : new Error('Failed to connect')
      );
    }
  }, []);

  const disconnect = useCallback(async () => {
    if (conversationRef.current) {
      await conversationRef.current.endSession();
      conversationRef.current = null;
    }
    setStatus('idle');
  }, []);

  const toggleMute = useCallback(() => {
    if (conversationRef.current) {
      const newMuted = !isMuted;
      conversationRef.current.setMicMuted(newMuted);
      setIsMuted(newMuted);
    }
  }, [isMuted]);

  const setMutedState = useCallback((muted: boolean) => {
    if (conversationRef.current) {
      conversationRef.current.setMicMuted(muted);
      setIsMuted(muted);
    }
  }, []);

  const getInputVolume = useCallback((): number => {
    if (!conversationRef.current) return 0;
    try {
      const volume = conversationRef.current.getInputVolume();
      return typeof volume === 'number' ? volume : 0;
    } catch {
      return 0;
    }
  }, []);

  const getOutputVolume = useCallback((): number => {
    if (!conversationRef.current) return 0;
    try {
      const volume = conversationRef.current.getOutputVolume();
      return typeof volume === 'number' ? volume : 0;
    } catch {
      return 0;
    }
  }, []);

  return {
    status,
    mode,
    isMuted,
    connect,
    disconnect,
    toggleMute,
    setMuted: setMutedState,
    getInputVolume,
    getOutputVolume,
  };
}
